import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, X, Download, Search } from 'lucide-react';

export default function IncidentalRate() {
  const [incidentalRecords, setIncidentalRecords] = useState([]);
  const [particulars, setParticulars] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [tableError, setTableError] = useState('');
  const [formError, setFormError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [role, setRole] = useState('');

  const [form, setForm] = useState({
    id: null,
    particular_id: '',
    commodity_id: '',
    season_id: '',
    rate: '',
    file: null,
  });

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    try {
      const [partRes, seasonRes, commRes] = await Promise.all([
        axios.get('particulars?paginate=false'),
        axios.get('seasons?paginate=false'),
        axios.get('commodity')
      ]);
      setParticulars(partRes.data.particulars || partRes.data.data || (Array.isArray(partRes.data) ? partRes.data : []));
      setSeasons(seasonRes.data.seasons || seasonRes.data.data || (Array.isArray(seasonRes.data) ? seasonRes.data : []));
      setCommodities(commRes.data.commodities || commRes.data.data || (Array.isArray(commRes.data) ? commRes.data : []));
    } catch (err) {
      console.error('Error fetching master data:', err);
    }
  };

  const fetchIncidentalRates = async (pageNum = 1, limitNum = 10, searchTerm = '') => {
    try {
      setLoading(true);
      let url = `incidental-rates?page=${pageNum}&limit=${limitNum}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setIncidentalRecords(res.data.incidentalRates || res.data.data || []);
      setPage(res.data.page || pageNum);
      setLimit(res.data.limit || limitNum);
      setTotal(res.data.total || 0);
      setTableError('');
    } catch (err) {
      console.error('Error fetching incidental rates:', err);
      // Mock data for UI demonstration
      setIncidentalRecords([
        { id: 1, particular: { name: 'MANDI LABOUR' }, season: { name: 'Rabi 2024' }, rate: 12.5 },
        { id: 2, particular: { name: 'INTERNAL TRANSPORT' }, season: { name: 'Kharif 2024' }, rate: 45.0 },
      ]);
      setTotal(2);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setRole(user?.roles?.[0]?.name || '');
    fetchData();
  }, []);

  useEffect(() => {
    if (fetchDataRef.current) {
      clearTimeout(fetchDataRef.current);
    }

    fetchDataRef.current = setTimeout(() => {
      fetchIncidentalRates(page, limit, search);
    }, 300);

    return () => {
      if (fetchDataRef.current) {
        clearTimeout(fetchDataRef.current);
      }
    };
  }, [page, limit, search]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setForm({ ...form, file: e.target.files[0] });
  };

  const handleAdd = () => {
    setForm({ id: null, particular_id: '', commodity_id: '', season_id: '', rate: '', file: null });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      particular_id: item.particular?.id || '',
      commodity_id: item.commodity?.id || '',
      season_id: item.season?.id || '',
      rate: item.rate,
      file: null,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.particular_id || !form.commodity_id || !form.season_id || !form.rate || (!form.id && !form.file)) {
      setFormError('All fields (including file for new records) are required');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('particular_id', form.particular_id);
      formData.append('commodity_id', form.commodity_id);
      formData.append('season_id', form.season_id);
      formData.append('rate', form.rate);
      if (form.file) {
        formData.append('file', form.file);
      }

      if (form.id) {
        await axios.patch(`incidental-rates/${form.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('✅ Incidental Rate updated successfully!');
      } else {
        await axios.post('incidental-rates', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('✅ Incidental Rate added successfully!');
      }
      setIsModalOpen(false);
      fetchIncidentalRates(page, limit, search);
    } catch (err) {
      console.error('Save error:', err);
      const errMsg =
        err.response?.data?.errors?.[0]?.error ||
        err.response?.data?.message ||
        'Failed to save Incidental Rate';
      setFormError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`incidental-rates/excel/download`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'incidental_rates.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
      alert('❌ Failed to export incidental rates');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-6 md:p-10 text-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
            📈 Incidental Rates
          </h2>
          <p className="text-gray-500 mt-1">Manage incidental rates per particular and season</p>
        </div>

        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          {role !== 'User' && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAdd}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 transition text-white px-4 py-2.5 rounded-lg shadow-md flex-1 md:flex-none"
            >
              <Plus size={18} />
              <span>Add New</span>
            </motion.button>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition text-white px-4 py-2.5 rounded-lg shadow-md flex-1 md:flex-none"
          >
            <Download size={18} />
            <span>Export Excel</span>
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={18} className="text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search by particular, commodity, or season..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
          value={search}
          onChange={handleSearch}
        />
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Particulars</th>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Commodity</th>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Season</th>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Rate (₹)</th>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Document</th>
                {role !== 'User' && (
                  <th className="py-4 px-6 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {incidentalRecords.length > 0 ? (
                incidentalRecords.map((item) => (
                  <tr key={item.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                    <td className="py-4 px-6 text-sm font-medium text-gray-800">{item.particular?.name}</td>
                    <td className="py-4 px-6 text-sm text-gray-700">{item.commodity?.commodityname}</td>
                    <td className="py-4 px-6 text-sm text-gray-700">{item.season?.name}</td>
                    <td className="py-4 px-6 text-sm font-bold text-blue-600">₹ {item.rate}</td>
                    <td className="py-4 px-6 text-sm">
                      {(() => {
                        const docs = Array.isArray(item.supporting_document)
                          ? item.supporting_document
                          : item.supporting_document
                          ? [item.supporting_document]
                          : [];
                        if (docs.length === 0) return <span className="text-gray-400">N/A</span>;
                        return (
                          <div className="flex flex-col gap-1">
                            {docs.map((url, di) => (
                              <a key={di} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                Doc {di + 1}
                              </a>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    {role !== 'User' && (
                      <td className="py-4 px-6 text-center">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50 transition"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </motion.button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={role !== 'User' ? 6 : 5} className="py-10 text-center text-gray-500">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 transition font-medium"
          >
            Previous
          </button>
          <span className="text-gray-600 font-medium">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 transition font-medium"
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="flex justify-between items-center bg-gray-50 px-6 py-4 border-b">
                <h3 className="text-xl font-bold text-gray-800">
                  {form.id ? 'Edit Incidental Rate' : 'Add New Incidental Rate'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100 font-medium">
                    {formError}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Particulars</label>
                  <select
                    name="particular_id"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.particular_id}
                    onChange={handleChange}
                  >
                    <option value="">Select Particular</option>
                    {particulars.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Commodity</label>
                  <select
                    name="commodity_id"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.commodity_id}
                    onChange={handleChange}
                  >
                    <option value="">Select Commodity</option>
                    {commodities.map((c) => (
                      <option key={c.id} value={c.id}>{c.commodityname}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Season</label>
                  <select
                    name="season_id"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.season_id}
                    onChange={handleChange}
                  >
                    <option value="">Select Season</option>
                    {seasons.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Rate (₹)</label>
                  <input
                    type="number"
                    name="rate"
                    placeholder="Enter rate"
                    step="0.01"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.rate}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Supporting Document {form.id ? '(Optional)' : '*'}</label>
                  <input
                    type="file"
                    name="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition bg-white"
                  />
                  {form.id && <p className="text-xs text-gray-500 mt-1">Leave empty to keep the existing document.</p>}
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 shadow-md transition disabled:opacity-70"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
